import { startOfMonth, endOfMonth } from 'date-fns';

import {
  Product,
  ProductInput,
  ProductCustomFields,
  ProductFeature,
  ProductStatus,
  SupportedProductPublicationStatus,
} from '@app/types/catalog';
import { MessageStatus, MessageTarget } from '@app/types/notifications';
import { OrderStatus } from '@app/types/order';

export const STORAGE_KEYS = {
  /*
    localStorage, sessionStorage を使う場合はここに key を追加
    重複しないように一元管理
  */

  ACCESS_TOKEN: 'accessToken',
  FINGERPRINT: 'fingerprint',
  REFRESH_TOKEN: 'refreshToken',
  USER_ID: 'userId',
};

export const prefectureData = [
  '北海道',
  '青森県',
  '岩手県',
  '宮城県',
  '秋田県',
  '山形県',
  '福島県',
  '茨城県',
  '栃木県',
  '群馬県',
  '埼玉県',
  '千葉県',
  '東京都',
  '神奈川県',
  '新潟県',
  '富山県',
  '石川県',
  '福井県',
  '山梨県',
  '長野県',
  '岐阜県',
  '静岡県',
  '愛知県',
  '三重県',
  '滋賀県',
  '京都府',
  '大阪府',
  '兵庫県',
  '奈良県',
  '和歌山県',
  '鳥取県',
  '島根県',
  '岡山県',
  '広島県',
  '山口県',
  '徳島県',
  '香川県',
  '愛媛県',
  '高知県',
  '福岡県',
  '佐賀県',
  '長崎県',
  '熊本県',
  '大分県',
  '宮崎県',
  '鹿児島県',
  '沖縄県',
];

export const defaultFeature: Array<ProductFeature> = [
  {
    name: '女性医療機関',
    value: '',
  },
  {
    name: '成果報酬型',
    value: '',
  },
  {
    name: '無料カウンセリング有り',
    value: '',
  },
  {
    name: '相談実績多数',
    value: '',
  },
  {
    name: '拠点多数',
    value: '',
  },
  {
    name: '認定資格有',
    value: '',
  },
];

export const localizedOrderStatus = {
  ACCEPTED: '受ける',
  CANCELED: '取消',
  CLOSED: '完了',
  PENDING: '未決',
  PROCESSING: '対応',
};

export const defaultLineItem = {
  price: {
    amount: 0,
    taxable: true,
  },
  productId: '',
  productName: '',
  quantity: 0,
  sku: '',
  variant: '',
  variantTitle: '',
};

export const defaultOrderStatusFilters = [
  OrderStatus.ACCEPTED,
  OrderStatus.CLOSED,
  OrderStatus.PENDING,
  OrderStatus.PROCESSING,
  OrderStatus.CANCELED,
];

export const defaultProductStatus = [
  ProductStatus.DRAFT,
  ProductStatus.ACTIVE,
  ProductStatus.ARCHIVED,
];

export const defaultProductCustomFields: ProductCustomFields = {
  access: '',
  annualSalaryNegotiable: false,
  breakTime: 0,
  breakTimeNoParticular: false,
  calculatedDairyPay: 0,
  calculatedHourlyPay: 0,
  catchphrase: '',
  commuting: '',
  commutingCost: 0,
  count: 1,
  day: '',
  endTime: '',
  incentive: '',
  orderConditions: '',
  organizationName: '',
  overWorkConditions: [],
  repeatDetail: '',
  repeatWeek: [],
  selection: '',
  startTime: '',
  workAddress1: '',
  workAddress2: '',
  workPostalCode: '',
  workPublicHoliday: false,
};

export const defaultProductValue: Product = {
  additionalInformation: '',
  attributes: [],
  category: {
    children: [],
    description: '',
    id: '',
    name: '',
    numberOfProducts: 0,
    parent: null,
    status: 'PENDING',
  },
  categoryId: '',
  customFields: { ...defaultProductCustomFields },
  description: '',
  dimensions: '',
  features: [],
  id: '',
  images: [],
  locations: [],
  name: '',
  organization: {},
  organizationId: '',
  publication: {
    publishedAt: '',
    publishedBy: '',
    since: startOfMonth(new Date()).toISOString(),
    status: 'DRAFT' as SupportedProductPublicationStatus,
    until: endOfMonth(new Date()).toISOString(),
  },
  tags: [],
  variantNote: '',
  variants: [],
};

export const defaultProductInput: ProductInput = {
  additionalInformation: '',
  attributes: [],
  categoryId: '',
  customFields: { ...defaultProductCustomFields },
  description: '',
  dimensions: '',
  features: [],
  imageIds: [],
  locationIds: [],
  name: '',
  publication: {
    since: startOfMonth(new Date()).toISOString(),
    status: 'DRAFT' as SupportedProductPublicationStatus,
    until: endOfMonth(new Date()).toISOString(),
  },
  tags: [],
  variantNote: '',
  variants: [],
};

export const productStatusList = [
  {
    id: 'ACTIVE',
    name: '掲載中',
  },
  {
    id: 'ARCHIVED',
    name: '終了',
  },
  {
    id: 'DRAFT',
    name: '下書き',
  },
];

export const productCategoryList = [
  {
    id: 'spot',
    name: 'スポット',
  },
  {
    id: 'regularPartTime',
    name: '定期非常勤',
  },
  {
    id: 'fullTime',
    name: '常勤',
  },
];

// TODO: 正式な利用規約URLが決まり次第差し替える（現状は仮リンク）
export const FULL_TIME_TERMS_URL =
  'https://med-pro.jp/media.dr/prodoctors_termsofservice/';

export const nurseProductCategoryList = [
  {
    id: 'spot',
    label: 'スポット',
    name: 'パラメディカル＞スポット',
  },
  {
    id: 'regularPartTime',
    label: '定期非常勤',
    name: 'パラメディカル＞定期非常勤',
  },
];

export const productEndPeriodStatusList = [
  {
    id: 'prev',
    name: '勤務日前日',
  },
  {
    id: 'daysAgo',
    name: '勤務日X日前',
  },
  {
    id: 'day',
    name: '日付指定',
  },
];

export const productVariantsSkuList = [
  {
    id: 'hour',
    name: '時給',
  },
  {
    id: 'day',
    name: '日給',
  },
];

export const productCommutingList = [
  {
    id: 'UpperLimit',
    name: '別途支給（上限金額）',
  },
  {
    id: 'NoUpperLimit',
    name: '別途支給（上限なし）',
  },
  {
    id: 'FixedFee',
    name: '別途支給（定額）',
  },
  {
    id: 'Include',
    name: '交通費込み（所定金額）',
  },
  {
    id: 'NoFee',
    name: 'なし',
  },
];

export const productSelectionList = [
  '',
  '対面面接必須',
  'オンライン面接可',
  '書類選考のみ',
];

// カルテ
export const SELECTABLE_CHARTS_TYPE = {
  ELECTRONIC: 'electronic',
  PAPER: 'paper',
};
export const CHARTS_LABEL = {
  [SELECTABLE_CHARTS_TYPE.ELECTRONIC]: '電子カルテ',
  [SELECTABLE_CHARTS_TYPE.PAPER]: '紙カルテ',
};

// 問い合わせ用 FORM
export const INQUIRY_FORM_URL = 'https://forms.gle/HhcHnK6rWTa9sVbL7'; // google forms

export const messageStatusTitles: { [key in MessageStatus]: string } = {
  CLOSE: '掲載終了',
  DRAFT: '下書き',
  PUBLISHED: '掲載中',
  WAITING: '掲載前',
};

export const messageStatusValues: MessageStatus[] = [
  'DRAFT',
  'WAITING',
  'PUBLISHED',
  'CLOSE',
];

export const messageStatusList: { id: MessageStatus; name: string }[] =
  messageStatusValues.map((v) => ({ id: v, name: messageStatusTitles[v] }));

export const messageTargetTitles: { [key in MessageTarget]: string } = {
  common: '共通',
  demand: 'ドクター向け',
  supply: 'クリニック向け',
};

export const notRequireClinicalDepartmentName = '専門科問わず';

// 年月日
export const dayOptions = Array.from({ length: 31 }, (val, i) => i + 1);
export const monthOptions = Array.from({ length: 12 }, (val, i) => i + 1);
const currentYear = new Date().getFullYear();
export const yearOptions = Array.from(
  { length: currentYear - 1900 + 1 },
  (val, i) => i + 1900
).reverse();

export const passwordPattern = /^(?=.*[a-zA-Z])(?=.*[0-9]).{8,24}$/;

export const MAX_FETCH_ORGANIZATION_SIZE = 50;
export const NO_TIME_UNTIL_DEFAULT_YEAR = '2099/12/31';
export const USER_ROLES_LABELS = {
  OWNER: '管理者',
};

/** 常勤求人の詳細情報に反映する入力例 */
export const FULL_TIME_DESCRIPTION_EXAMPLE = `------------------------------------------
■ 求人概要
------------------------------------------
▼勤務日数
・［例：週3日から相談可能］
・［例：時短勤務相談可能］

▼勤務曜日
［例：金曜日・土曜日を含めて勤務可能な方］
［例：その他の勤務曜日についてはご相談可能です。］

▼勤務時間
平日：［開始時刻］～［終了時刻］（休憩［○］分）
土曜日：［開始時刻］～［終了時刻］（休憩［○］分）
日曜日・祝日：［勤務有無／時間］

※［補足\u3000例：具体的な勤務時間は勤務曜日等に応じてご相談いたします。］
※［補足\u3000例：日曜日に勤務可能な方は優遇いたします。］

▼業務内容
【外来診療】
・［例：一般内科外来（生活習慣病、感冒、腹痛等の急性疾患）］
・［例：専門外来（〇〇疾患の診断・治療・継続管理）］

【検査・手技】
・［例：上部・下部消化管内視鏡検査、ポリープ切除（日帰り手術）］
・［例：腹部エコー、甲状腺エコー等の超音波検査］

【その他の業務】
・［例：高次医療機関への紹介状作成、逆紹介患者の受け入れ］
・［例：専門外来の立ち上げ・運営へのご協力（ご希望に応じて）］

【曜日ごとの担当】
・平日：［例：外来診療＋内視鏡検査］
・土曜日：［例：外来診療を中心に担当］

【患者層】
・［例：20～40代の働く世代が中心、近隣の高齢者も多数］
・［例：健診で異常を指摘された方の精査目的の来院が多い］

【サポート体制】
・［例：内視鏡介助は経験豊富な看護師・技師が担当］
・［例：医療クラークによる電子カルテ入力サポートあり］

▼診療数・診療割合の目安
外来：1コマあたり［○～○］名程度
検査・処置：［例：1コマ○～○件程度］
診療割合：［例：入職当初は一般内科 約○％／専門診療 約○％を想定］
［補足\u3000例：患者数の増加に伴い、専門診療の割合を高めていく予定です。］

▼給与
・週3日：年収［○,○○○］万円～［○,○○○］万円
・週4日：年収［○,○○○］万円～［○,○○○］万円
※経験・専門性・勤務内容等を踏まえて決定いたします。

▼必須要件
・［例：日本〇〇学会 〇〇専門医］

▼優遇要件
・［例：日本〇〇学会 〇〇専門医］
・［例：〇〇（手技・検査名）に対応可能な方］

------------------------------------------
■ 福利厚生・勤務環境
------------------------------------------
・［例：医師国保／医師賠償責任保険完備］
・［例：学会参加・学会費等の補助制度あり］

------------------------------------------
■ 医療機関情報
------------------------------------------
【基本情報】
・診療科目：［例：消化器内科、内科］
・診療時間：［例：平日 9:00～18:30／土曜 9:00～13:30］
・医師体制：［例：常勤○名、非常勤○名］
・主な設備：［例：内視鏡システム、エコー］
・1日平均外来患者数：［○○］名程度

【当院の特徴】
・診療理念：［例：「苦痛の少ない検査」と「丁寧な説明」を大切にしています。］
・院内の雰囲気：［例：医師・スタッフ間の距離が近く、気軽に相談できる風通しの良い職場です。］
・チーム体制：［例：看護師・検査技師・受付が連携し、医師が診療に集中できる体制を整えています。］
・働きやすさ：［例：残業はほとんどなく、プライベートとの両立がしやすい環境です。］
・専門性の発揮：［例：ご専門に応じて、専門外来の拡充や新たな検査の導入もご相談いただけます。］
・地域との関わり：［例：駅前立地で幅広い患者様が来院され、近隣病院との連携も良好です。］`;
